import React, { FC, useState, useEffect } from 'react';
import { useGetProgramMasterData } from 'src/api/mutations/useGetProgramMasterData';
import './IncreaseStrengthSteps.scss';
import './WeightLossStep3.scss';

interface WeightLossStep3Props {
  value?: { target: number; unit: string };
  onChange?: (value: { target: number; unit: string }) => void;
}

// What is the weight loss target?
export const WeightLossStep3: FC<WeightLossStep3Props> = ({ value, onChange }) => {
  const { data: programMasterData, isLoading, isError } = useGetProgramMasterData();
  const targetData = programMasterData?.weightLossTarget;

  const apiMin = targetData?.slider?.min || 40;
  const apiMax = targetData?.slider?.max || 80;

  const [unit, setUnit] = useState<string>(value?.unit || 'Kg');
  const [weight, setWeight] = useState<number>(
    value?.target || (unit === 'Lb' ? Math.round(apiMin * 2.2) : apiMin),
  );

  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startWeight, setStartWeight] = useState(0);

  const minWeight = unit === 'Kg' ? apiMin : Math.round(apiMin * 2.2);
  const maxWeight = unit === 'Kg' ? apiMax : Math.round(apiMax * 2.2);

  const handleWeightChange = (delta: number) => {
    let newWeight = weight + delta;
    if (newWeight < minWeight) newWeight = minWeight;
    if (newWeight > maxWeight) newWeight = maxWeight;

    if (newWeight !== weight) {
      setWeight(newWeight);
      if (onChange) onChange({ target: newWeight, unit });
    }
  };

  const handleUnitChange = (newUnit: string) => {
    if (newUnit === unit) return;

    let newWeight = weight;
    if (newUnit === 'Kg') {
      newWeight = Math.round(weight / 2.2);
    } else {
      newWeight = Math.round(weight * 2.2);
    }

    const newMin = newUnit === 'Kg' ? apiMin : Math.round(apiMin * 2.2);
    const newMax = newUnit === 'Kg' ? apiMax : Math.round(apiMax * 2.2);

    if (newWeight < newMin) newWeight = newMin;
    if (newWeight > newMax) newWeight = newMax;

    setUnit(newUnit);
    setWeight(newWeight);
    if (onChange) onChange({ target: newWeight, unit: newUnit });
  };

  const ticks = [];
  for (let i = minWeight; i <= maxWeight; i++) {
    ticks.push(i);
  }

  const [tickWidth, setTickWidth] = useState(window.innerWidth <= 768 ? 8 : 10);

  useEffect(() => {
    const handleResize = () => setTickWidth(window.innerWidth <= 768 ? 8 : 10);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const shift = (weight - minWeight) * tickWidth;

  const handlePointerDown = (clientX: number) => {
    setIsDragging(true);
    setStartX(clientX);
    setStartWeight(weight);
  };

  const handlePointerMove = (clientX: number) => {
    if (!isDragging) return;
    const deltaX = clientX - startX;
    const deltaWeight = Math.round(deltaX / tickWidth);
    let newWeight = startWeight - deltaWeight;

    if (newWeight < minWeight) newWeight = minWeight;
    if (newWeight > maxWeight) newWeight = maxWeight;

    if (newWeight !== weight) {
      setWeight(newWeight);
      if (onChange) onChange({ target: newWeight, unit });
    }
  };

  const handlePointerUp = () => {
    setIsDragging(false);
  };

  if (isLoading) {
    return <div className="increase-strength-step">{'Loading options...'}</div>;
  }

  if (isError || !targetData) {
    return <div className="increase-strength-step">{'Error loading options.'}</div>;
  }

  return (
    <div className="increase-strength-step">
      <h2 className="step-title">{targetData?.title}</h2>

      <div
        className="weight-target-container"
        style={{ margin: '40px auto', textAlign: 'center', width: '100%' }}
      >
        <div className="tab-toggle">
          <button
            className={`tab ${unit === 'Kg' ? 'active' : ''}`}
            onClick={() => handleUnitChange('Kg')}
          >
            Kg
          </button>
          <button
            className={`tab ${unit === 'Lb' ? 'active' : ''}`}
            onClick={() => handleUnitChange('Lb')}
          >
            Lb
          </button>
        </div>

        <div className="weight-ruler-container">
          <button
            className="ruler-nav-btn"
            onClick={() => handleWeightChange(-1)}
            disabled={weight <= minWeight}
          >
            <svg
              width={14}
              height={24}
              viewBox="0 0 14 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10 20.9583L2 12.2917L10 3.625"
                stroke="white"
                strokeOpacity="0.7"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          <div
            className="ruler-viewport"
            style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
            onMouseDown={(e) => handlePointerDown(e.clientX)}
            onMouseMove={(e) => handlePointerMove(e.clientX)}
            onMouseUp={handlePointerUp}
            onMouseLeave={handlePointerUp}
            onTouchStart={(e) => handlePointerDown(e.touches[0].clientX)}
            onTouchMove={(e) => handlePointerMove(e.touches[0].clientX)}
            onTouchEnd={handlePointerUp}
            onTouchCancel={handlePointerUp}
          >
            <div className="ruler-borders"></div>
            <div className="ruler-center-pointer"></div>
            <div
              className="ruler-track"
              style={{
                transform: `translateX(calc(-${shift}px - ${tickWidth / 2}px))`,
                transition: isDragging ? 'none' : 'transform 0.3s ease-out',
              }}
            >
              {ticks.map((t) => {
                const isMajor = t % 5 === 0;
                const isActive = t === weight;
                const dist = Math.abs(t - weight);
                let distClass = 'dist-far';
                if (dist === 0) distClass = 'dist-0';
                else if (dist <= 10) distClass = 'dist-10';

                return (
                  <div
                    key={t}
                    className={`ruler-tick ${isMajor ? 'major' : 'minor'} ${isActive ? 'active' : ''} ${distClass}`}
                  >
                    {isMajor && <div className="tick-number">{t}</div>}
                    <div className="tick-line"></div>
                  </div>
                );
              })}
            </div>
          </div>

          <button
            className="ruler-nav-btn"
            onClick={() => handleWeightChange(1)}
            disabled={weight >= maxWeight}
          >
            <svg
              width={14}
              height={24}
              viewBox="0 0 14 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3.33398 20.9583L11.334 12.2917L3.33398 3.625"
                stroke="white"
                strokeOpacity="0.7"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
        <div className="weight-active-display">{weight}</div>
      </div>
    </div>
  );
};
